import { HttpStatusCode } from '@angular/common/http';
import {
  ChangeDetectorRef,
  Component,
  HostBinding,
  inject,
  input,
  signal,
  ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { RouterLink } from '@angular/router';
import { GhostPlayerComponent } from '@components/athena-player/ghost-player/ghost-player.component';
import { SheydaBadgeComponent } from '@components/badges/sheyda-badge/sheyda-badge.component';
import { IconButtonComponent } from '@components/buttons/icon-button/icon-button.component';
import {
  AccessabilityDialogComponent,
  AccessabilityDialogData,
} from '@components/dialogs/accessability-dialog/accessability-dialog.component';
import { ImageGeneratorComponent } from '@components/image-generator/image-generator.component';
import {
  EpisodeAccessType,
  InterestAction,
  InterestType,
  PlayableTrailer,
  Program,
  ProgramWithSeasons,
} from '@shared/graphql/api.generated';
import { variables } from '@shared/helper/configs';
import {
  cardTypographySP,
  expandableCardCarouselSP,
  expandableCardGridSP,
} from '@shared/helper/responsive-sizes';
import { SnackbarService, SnackbarType } from '@shared/helper/snackbar.service';
import { EpisodeService } from '@shared/services/episode.service';
import { GeneralService } from '@shared/services/general.service';
import { InterestService } from '@shared/services/interest.service';
import { ProgramService } from '@shared/services/program.service';
import { TokenService } from '@shared/services/token.service';
import { TrailerService } from '@shared/services/trailer.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-sheyda-program-card',
  imports: [
    ImageGeneratorComponent,
    RouterLink,
    IconButtonComponent,
    SheydaBadgeComponent,
    GhostPlayerComponent,
  ],
  providers: [ProgramService, EpisodeService, InterestService],
  templateUrl: './sheyda-program-card.component.html',
  styleUrl: './sheyda-program-card.component.scss',
})
export class SheydaProgramCardComponent {
  generalService = inject(GeneralService);
  private readonly snackbar = inject(SnackbarService);
  private readonly programService = inject(ProgramService);
  private readonly episodeService = inject(EpisodeService);
  private readonly trailerService = inject(TrailerService);
  private readonly interestService = inject(InterestService);
  private readonly tokenService = inject(TokenService);
  private readonly dialog = inject(MatDialog);
  private readonly cdr = inject(ChangeDetectorRef);

  @HostBinding('class.hovered') public isHovered = false;
  @ViewChild('ghostPlayer') ghostPlayer!: GhostPlayerComponent;
  Item = input.required<Program>();
  ViewType = input<'carousel' | 'grid'>();
  program = signal<Program | undefined>(undefined);
  programDetails = signal<ProgramWithSeasons | undefined>(undefined);
  viewableTrailer = signal<PlayableTrailer | undefined>(undefined);
  showPreviewDialog = signal(false);
  submitBookmark = signal(false);
  isShowPreview = signal(false);
  isViewPreview = signal(false);
  isMouseOnCard = signal(false);
  actionButtonType = signal<'repeat' | 'mute' | 'unmute' | null>(null);
  showInterestSection = signal(false);
  selectedInterest = signal<InterestAction | null>(null);
  submitInterest = signal(false);
  imageSizes = this.ViewType() === 'carousel' ? expandableCardCarouselSP : expandableCardGridSP;
  interestAction = InterestAction;
  accessTypes = EpisodeAccessType;
  cardTypographySizes = cardTypographySP;

  private getProgramDetails$!: Subscription;
  private showInterestTimer: any;
  private showPreviewTimer: any;
  private timeoutTimer: any;
  private alreadyEnterOnCard = signal(false);

  handleInitialSetMute(e: boolean) {
    this.actionButtonType.set(e ? 'mute' : 'unmute');
  }

  handleEnterOnCard() {
    if (!this.alreadyEnterOnCard()) {
      this.isMouseOnCard.set(true);
      this.isHovered = this.isMouseOnCard();
      this.cdr.detectChanges();
      this.timeoutTimer = setTimeout(() => {
        if (!this.isMouseOnCard()) return;
        this.alreadyEnterOnCard.set(true);
        this.showPreviewDialog.set(true);
        this.getProgramDetails();
      }, 300);
    }
  }

  handleLeaveOfCard() {
    clearTimeout(this.timeoutTimer);
    this.isMouseOnCard.set(false);
    this.isHovered = this.isMouseOnCard();
    this.showPreviewDialog.set(false);
    this.alreadyEnterOnCard.set(false);
    this.isShowPreview.set(false);
    this.program.set(undefined);
    this.isViewPreview.set(false);
    const c = this.trailerService.ghostPlayerComponent$();
    if (c?.athenaPlayer) {
      if (c.athenaPlayer.getCurrentTime() < c.athenaPlayer.getVideoDuration() - 0.5) {
        c.athenaPlayer.play();
      }
    }
    if (this.getProgramDetails$) {
      this.getProgramDetails$.unsubscribe();
    }
    if (this.showPreviewTimer) {
      clearTimeout(this.showPreviewTimer);
    }
    if (this.ghostPlayer) {
      this.ghostPlayer.destroy();
    }
  }

  getProgramDetails() {
    this.getProgramDetails$ = this.programService
      .getProgramDetailsInMiniDialogByUid(this.Item().uid)
      .subscribe({
        next: (res) => {
          this.program.set(res.data?.program);
          this.programDetails.set(res.data!);
          this.selectedInterest.set(this.program()!.interestDetails?.interestedByUser!);
          if (this.programDetails()?.trailers?.length) {
            this.viewableTrailer.set(
              this.programDetails()!
                .trailers.filter((item) => item?.isDefault)
                .at(0)!,
            );
            if (!this.viewableTrailer()) {
              this.viewableTrailer.set(this.programDetails()!.trailers.at(0)!);
            }
            if (this.viewableTrailer()) {
              this.isShowPreview.set(true);
              this.showPreviewTimer = setTimeout(() => {
                setTimeout(() => {
                  if (this.ghostPlayer) {
                    this.ghostPlayer.initialPlayer(this.viewableTrailer()!.playLinkURL);
                    this.handlePlayerVolume();
                  }
                  const c = this.trailerService.ghostPlayerComponent$();
                  if (c?.athenaPlayer) {
                    c.athenaPlayer.pause();
                    this.handlePlayerVolume();
                  }
                  this.isViewPreview.set(true);
                });
              }, 300);
            }
          }
        },
      });
  }

  handlePlayProgram() {
    this.handleLeaveOfCard();
    if (this.episodeService.checkEpisodeDisplay(this.programDetails()?.solitaryEpisode!)) {
      this.generalService.router.navigateByUrl(`/w/${this.programDetails()!.solitaryEpisode!.uid}`);
    }
  }

  handleBookmark() {
    if (this.tokenService.getRtk()) {
      this.submitBookmark.set(true);
      this.programService.toggleBookmark(this.Item().id).subscribe({
        next: (res) => {
          this.submitBookmark.set(false);
          this.program.update((item) => {
            if (item) {
              return {
                ...item,
                isBookMarkedByMe: !item.isBookMarkedByMe,
              };
            }
            return undefined;
          });
          this.snackbar.showSnackbar(res.status.message);
        },
        error: (e) => {
          this.submitBookmark.set(false);
          this.snackbar.showSnackbar(e.networkError.message, SnackbarType.error);
          this.handleLeaveOfCard();
        },
      });
    } else {
      const data: AccessabilityDialogData = {
        message: 'برای اضافه کردن به لیست من، ابتدا باید وارد شوید',
      };
      this.dialog.open(AccessabilityDialogComponent, {
        data,
      });
    }
  }

  handleHoverOnLikeBtn() {
    this.showInterestTimer = setTimeout(() => {
      this.showInterestSection.set(true);
    }, 100);
  }

  handleShowHoveredElement() {
    this.showInterestSection.set(true);
  }

  handleLeaveHoveredElement() {
    this.showInterestSection.set(false);
  }

  handleLeaveOnLikeBtn() {
    this.handleLeaveHoveredElement();
    if (this.showInterestTimer) {
      clearTimeout(this.showInterestTimer);
    }
  }

  handleInterestAction(action: InterestAction) {
    this.submitInterest.set(true);
    this.showInterestSection.set(false);
    if (this.selectedInterest() === action) {
      this.removeInterest();
    } else {
      this.addInterest(action);
    }
  }

  addInterest(action: InterestAction) {
    if (this.tokenService.getRtk()) {
      this.interestService.addInterest(this.program()!.id, action, InterestType.Program).subscribe({
        next: (res) => {
          this.submitInterest.set(false);
          this.snackbar.showSnackbar(res.status.message);
          if (res.status.statusCode === HttpStatusCode.Ok) {
            this.selectedInterest.set(action);
            this.showInterestSection.set(false);
          }
        },
        error: (e) => {
          this.submitInterest.set(false);
          this.snackbar.showSnackbar(e.networkError.message, SnackbarType.error);
        },
      });
    } else {
      this.submitInterest.set(false);
      const data: AccessabilityDialogData = {
        message: 'برای ثبت علاقه‌مندی، ابتدا باید وارد شوید',
      };
      this.dialog.open(AccessabilityDialogComponent, {
        data,
      });
    }
  }

  removeInterest() {
    this.interestService.removeInterest(this.program()!.id, InterestType.Program).subscribe({
      next: (res) => {
        this.submitInterest.set(false);
        this.snackbar.showSnackbar(res.status.message);
        if (res.status.statusCode === HttpStatusCode.Ok) {
          this.selectedInterest.set(null);
          this.showInterestSection.set(false);
        }
      },
      error: (e) => {
        this.submitInterest.set(false);
        this.snackbar.showSnackbar(e.networkError.message, SnackbarType.error);
      },
    });
  }

  handleEndedTeaser(e: boolean) {
    if (e) {
      this.isViewPreview.set(false);
      this.actionButtonType.set('repeat');
    }
  }

  replayTrailer() {
    if (this.isShowPreview()) {
      if (this.ghostPlayer?.athenaPlayer) {
        this.isViewPreview.set(true);
        this.ghostPlayer.athenaPlayer.play();
        this.handlePlayerVolume();
      }
    }
  }

  muteTrailer() {
    if (this.generalService.isBrowserOnly) {
      if (this.isShowPreview()) {
        localStorage.setItem(variables.playerMuted, '1');
        this.actionButtonType.set('mute');
        if (this.ghostPlayer?.athenaPlayer) {
          this.ghostPlayer.athenaPlayer.mute();
        }
      }
    }
  }

  unMuteTrailer() {
    if (this.generalService.isBrowserOnly) {
      if (this.isShowPreview()) {
        localStorage.setItem(variables.playerMuted, '0');
        this.actionButtonType.set('unmute');
        if (this.ghostPlayer?.athenaPlayer) {
          this.ghostPlayer.athenaPlayer.unmute();
        }
      }
    }
  }

  handlePlayerVolume() {
    if (this.generalService.isBrowserOnly) {
      const isMuted = localStorage.getItem(variables.playerMuted);
      if (isMuted) {
        if (isMuted === '1') {
          this.muteTrailer();
        } else {
          this.unMuteTrailer();
        }
      } else {
        this.unMuteTrailer();
      }
    }
  }

  ngOnDestroy() {
    this.handleLeaveOfCard();
  }
}
